import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { getAddonPricing, createAddon, AddonPricing } from '../../services/omiseService';
import { CheckCircle, AlertCircle, ArrowLeft, Loader2, Package, Database } from 'lucide-react';
import SEO from '../../components/SEO';

export default function OmiseAddonCheckout() {
  const [searchParams] = useSearchParams();
  const billingCycle = searchParams.get('billing') as 'monthly' | 'yearly' || 'monthly';
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [addons, setAddons] = useState<AddonPricing[]>([]);
  const [selectedAddon, setSelectedAddon] = useState<AddonPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/checkout/addon?billing=' + billingCycle);
      return;
    }

    loadAddons();
  }, [user, billingCycle, navigate]);

  const loadAddons = async () => {
    try {
      setLoading(true);
      const addonsData = await getAddonPricing(billingCycle);
      setAddons(addonsData);
    } catch (err: any) {
      console.error('Error loading addons:', err);
      setError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAddon = (addon: AddonPricing) => {
    setSelectedAddon(addon);
    setError('');
  };

  const handlePurchase = async () => {
    if (!selectedAddon) {
      setError('กรุณาเลือก Add-on');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      console.log('Creating addon...');
      const result = await createAddon(selectedAddon.addon_id, billingCycle);
      console.log('Addon result:', result);

      if (result.authorize_uri) {
        window.location.href = result.authorize_uri;
        return;
      }

      if (result.status === 'successful' && result.chargeId) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/payment/omise/success?charge_id=${result.chargeId}&type=addon`);
        }, 1500);
      } else {
        setError('การชำระเงินไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err: any) {
      console.error('Purchase error:', err);
      setError(err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setProcessing(false);
    }
  };

  const getPrice = (addon: AddonPricing) => {
    return billingCycle === 'yearly' ? addon.price_yearly : addon.price_monthly;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">ชำระเงินสำเร็จ!</h2>
          <p className="text-gray-600 mb-4">
            เพิ่มพื้นที่จัดเก็บเรียบร้อยแล้ว
          </p>
          <p className="text-sm text-gray-500">กำลังเปลี่ยนเส้นทาง...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="เพิ่มพื้นที่จัดเก็บ (Add-on) - EduMi"
        description="เพิ่มพื้นที่จัดเก็บสำหรับคอร์สของคุณ"
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>ย้อนกลับ</span>
          </button>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center">
            เพิ่มพื้นที่จัดเก็บ (Add-on)
          </h1>
          <p className="text-center text-gray-600 mb-8">
            เลือกขนาดพื้นที่เพิ่มเติมที่เหมาะกับความต้องการของคุณ
          </p>

          <div className="mb-6 flex justify-center gap-4">
            <button
              onClick={() => navigate('/checkout/addon?billing=monthly')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-500'
              }`}
            >
              รายเดือน
            </button>
            <button
              onClick={() => navigate('/checkout/addon?billing=yearly')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all relative ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-500'
              }`}
            >
              รายปี
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                ฟรี 2 เดือน
              </span>
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {addons.map((addon) => {
              const price = getPrice(addon);
              const isSelected = selectedAddon?.addon_id === addon.addon_id;

              return (
                <div
                  key={addon.addon_id}
                  onClick={() => handleSelectAddon(addon)}
                  className={`bg-white rounded-2xl shadow-lg p-6 cursor-pointer transition-all hover:shadow-xl ${
                    isSelected
                      ? 'ring-4 ring-blue-500 transform scale-105'
                      : 'hover:ring-2 hover:ring-purple-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Database className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{addon.name}</h3>
                        <p className="text-sm text-gray-600">เพิ่มพื้นที่จัดเก็บ {addon.storage_gb}GB</p>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-8 h-8 text-blue-600" />
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-blue-600">฿{price.toLocaleString()}</span>
                      <span className="text-gray-600">/{billingCycle === 'yearly' ? 'ปี' : 'เดือน'}</span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>เพิ่มพื้นที่ {addon.storage_gb}GB</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>ใช้ได้ทั้งวิดีโอและไฟล์</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>ต่ออายุอัตโนมัติ</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3 max-w-2xl mx-auto">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {selectedAddon && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">สรุปการสั่งซื้อ</h3>
                
                <div className="space-y-3 mb-4 pb-4 border-b">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Add-on</span>
                    <span className="font-semibold text-gray-900">{selectedAddon.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">พื้นที่เพิ่ม</span>
                    <span className="font-semibold text-gray-900">{selectedAddon.storage_gb} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">รอบบิล</span>
                    <span className="font-semibold text-gray-900">
                      {billingCycle === 'yearly' ? 'รายปี' : 'รายเดือน'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-6">
                  <span className="text-lg font-bold text-gray-900">ยอดรวม</span>
                  <span className="text-2xl font-bold text-blue-600">
                    ฿{getPrice(selectedAddon).toLocaleString()}
                  </span>
                </div>

                <button
                  onClick={handlePurchase}
                  disabled={processing}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      กำลังดำเนินการ...
                    </>
                  ) : (
                    <>
                      <Package className="w-5 h-5" />
                      ชำระเงิน ฿{getPrice(selectedAddon).toLocaleString()}
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  * ใช้บัตรเครดิต/เดบิตที่ผูกไว้กับบัญชี
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
